import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../ui/sheet";
import { Slider } from "../ui/slider";
import { SlidersHorizontal, Check } from "lucide-react";
import { useState, useEffect } from "react";

export default function FilterSheet({ facets, value, onApply }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);

    useEffect(() => { setDraft(value); }, [value, open]);

    const minP = Math.floor(facets?.min_price || 0);
    const maxP = Math.ceil(facets?.max_price || 50000);

    const apply = () => { onApply(draft); setOpen(false); };
    const reset = () => {
        const empty = { occasion: "", fabric: "", brand: "", color: "", min_price: minP, max_price: maxP, trending: false, new_arrivals: false };
        setDraft(empty);
        onApply(empty);
        setOpen(false);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <button className="ayraa-btn-outline py-2 px-4 text-sm" data-testid="open-filters-btn">
                    <SlidersHorizontal className="w-4 h-4 mr-2" strokeWidth={1.5} /> Filters
                </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-brand-paper border-brand-border max-h-[88vh] overflow-y-auto">
                <SheetHeader><SheetTitle className="font-serif text-2xl text-left">Refine</SheetTitle></SheetHeader>
                <div className="space-y-7 mt-4 pb-32">
                    <Group label="Occasion" items={facets.occasions} value={draft.occasion} onPick={(v) => setDraft({ ...draft, occasion: draft.occasion === v ? "" : v })} cap />
                    <Group label="Fabric" items={facets.fabrics} value={draft.fabric} onPick={(v) => setDraft({ ...draft, fabric: draft.fabric === v ? "" : v })} />
                    <Group label="Brand" items={facets.brands} value={draft.brand} onPick={(v) => setDraft({ ...draft, brand: draft.brand === v ? "" : v })} />
                    <Group label="Color" items={facets.colors} value={draft.color} onPick={(v) => setDraft({ ...draft, color: draft.color === v ? "" : v })} />

                    <div>
                        <div className="ayraa-overline">Price ₹{Number(draft.min_price ?? minP).toLocaleString("en-IN")} – ₹{Number(draft.max_price ?? maxP).toLocaleString("en-IN")}</div>
                        <div className="mt-4 px-1">
                            <Slider
                                min={minP}
                                max={maxP || 1}
                                step={Math.max(100, Math.floor((maxP - minP) / 50))}
                                value={[draft.min_price ?? minP, draft.max_price ?? maxP]}
                                onValueChange={(v) => setDraft({ ...draft, min_price: v[0], max_price: v[1] })}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Toggle label="Trending" on={!!draft.trending} onChange={(v) => setDraft({ ...draft, trending: v })} />
                        <Toggle label="New arrivals" on={!!draft.new_arrivals} onChange={(v) => setDraft({ ...draft, new_arrivals: v })} />
                    </div>
                </div>
                <SheetFooter className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-xl border-t border-brand-border flex flex-row gap-3">
                    <button className="ayraa-btn-outline flex-1" onClick={reset} data-testid="filter-reset-btn">Reset</button>
                    <button className="ayraa-btn-primary flex-1" onClick={apply} data-testid="filter-apply-btn">Apply</button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

function Group({ label, items, value, onPick, cap }) {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <div className="ayraa-overline">{label}</div>
            <div className="mt-3 flex flex-wrap gap-2">
                {items.map((it) => {
                    const sel = value === it;
                    const text = cap ? it.charAt(0).toUpperCase() + it.slice(1) : it;
                    return (
                        <button
                            key={it}
                            onClick={() => onPick(it)}
                            className={`text-sm px-3 py-1.5 rounded-full border transition ${sel ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-border text-brand-dark hover:border-brand-gold"}`}
                            data-testid={`filter-${label.toLowerCase()}-${it}`}
                        >
                            {sel ? <Check className="w-3 h-3 inline mr-1" strokeWidth={2} /> : null}
                            {text}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function Toggle({ label, on, onChange }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!on)}
            className={`text-sm px-4 py-2 rounded-full border transition ${on ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-border text-brand-dark hover:border-brand-gold"}`}
        >
            {label}
        </button>
    );
}
